import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Package, Plus, Minus, Edit, Search, AlertTriangle, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { materialsApi, apiClient } from '@/services/api'

interface Material {
  id: string
  name: string
  // Template-based properties only
  templateCategoryId: string
  templateCategory: {
    id: string
    name: string
    description?: string
  }
  templateUnitTypeId: string
  templateUnitType: {
    id: string
    name: string
    abbreviation: string
  }
  currentStock: number
  reorderPoint: number
  unitCost?: number  // Cost per unit for financial calculations (may not exist)
  lastPurchasePrice?: number  // From backend entity
  lastCostPerUnit?: number  // From backend entity
  averageCost?: number  // From backend entity - this is the main cost field
  color?: string
  size?: string
  brand?: string  // Brand information
  sku?: string  // SKU for identification
  description?: string  // Material description
  imageUrls: string[]
  // Additional fields from backend
  specifications?: {
    color?: string
    gauge?: string
    diameter?: string
    length?: string
    finish?: string
    material?: string
    weight?: string
    [key: string]: any
  }
}

interface StockAdjustment {
  materialId: string
  adjustmentType: 'increase' | 'decrease' | 'set'
  quantity: number
  reason: string
  notes?: string
  // Financial tracking fields
  unitCost: number
  totalCostImpact: number
  isWriteOff: boolean
  writeOffCategory?: string
  adjustmentDate: string
  adjustedBy: string
}

interface MaterialCategoryOption {
  id: string
  name: string
}

const ADJUSTMENT_REASONS = [
  'Physical count correction',
  'Damaged materials',
  'Lost materials',
  'Found materials',
  'Supplier correction',
  'Quality control rejection',
  'Transfer between locations',
  'Other'
]

const WRITE_OFF_CATEGORIES = [
  'Damaged inventory',
  'Obsolete inventory',
  'Theft/Loss',
  'Quality control failure',
  'Expired materials',
  'Supplier defect',
  'Manufacturing waste',
  'Other business loss'
]

export const MaterialsStockAdjustmentPage: React.FC = () => {
  const navigate = useNavigate()
  const [materials, setMaterials] = useState<Material[]>([])
  const [filteredMaterials, setFilteredMaterials] = useState<Material[]>([])
  const [materialCategories, setMaterialCategories] = useState<MaterialCategoryOption[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [stockFilter, setStockFilter] = useState('')
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null)
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease' | 'set'>('increase')
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [isWriteOff, setIsWriteOff] = useState(false)
  const [writeOffCategory, setWriteOffCategory] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    fetchMaterials()
    fetchMaterialCategories()
  }, [])

  const fetchMaterialCategories = async (retryCount = 0) => {
    try {
      // Use template-based form config endpoint
      const response = await apiClient.get('/templates/user/form-config')
      if (response && response.categories && Array.isArray(response.categories)) {
        setMaterialCategories(response.categories)
      } else if (response && response.data && response.data.categories && Array.isArray(response.data.categories)) {
        setMaterialCategories(response.data.categories)
      } else {
        setMaterialCategories([])
      }
    } catch (error: any) {
      console.error('Error fetching template categories:', error)
      
      // Retry on network errors (like ETIMEDOUT) up to 2 times
      if (retryCount < 2 && (error?.code === 'ETIMEDOUT' || error?.message?.includes('timeout'))) {
        console.log(`Retrying categories fetch (attempt ${retryCount + 1})...`)
        setTimeout(() => fetchMaterialCategories(retryCount + 1), 1000 * (retryCount + 1))
        return
      }
      
      setMaterialCategories([])
    }
  }

  useEffect(() => {
    let filtered = materials.filter(material => {
      // Enhanced search filter with null safety - include brand, SKU, and specifications
      const materialName = material.name || ''
      // Use template category only
      const materialCategory = material.templateCategory?.name || ''
      const materialColor = material.color || material.specifications?.color || ''
      const materialBrand = material.brand || ''
      const materialSku = material.sku || ''
      const materialDescription = material.description || ''
      
      // Search across multiple fields for better material identification
      const matchesSearch = searchTerm === '' ||
        materialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        materialCategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
        materialColor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        materialBrand.toLowerCase().includes(searchTerm.toLowerCase()) ||
        materialSku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        materialDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        // Also search in specifications
        Object.values(material.specifications || {}).some(spec =>
          spec && spec.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      
      // Category filter - match by template category ID only
      let matchesCategory = true
      if (categoryFilter && categoryFilter !== '') {
        const selectedCategory = materialCategories.find(cat => cat.name === categoryFilter)
        if (selectedCategory) {
          matchesCategory = material.templateCategoryId === selectedCategory.id
        } else {
          matchesCategory = materialCategory.toLowerCase() === categoryFilter.toLowerCase()
        }
      }
      
      // Stock level filter with null safety
      let matchesStock = true
      const currentStock = material.currentStock || 0
      const reorderPoint = material.reorderPoint || 0
      
      if (stockFilter === 'low') {
        matchesStock = currentStock <= reorderPoint
      } else if (stockFilter === 'out') {
        matchesStock = currentStock === 0
      } else if (stockFilter === 'normal') {
        matchesStock = currentStock > reorderPoint
      }

      return matchesSearch && matchesCategory && matchesStock
    })

    setFilteredMaterials(filtered)
  }, [materials, searchTerm, categoryFilter, stockFilter])

  const fetchMaterials = async () => {
    try {
      setIsLoading(true)
      // Use the optimized stock-adjustment endpoint for faster loading
      const response = await apiClient.get('/materials/stock-adjustment-optimized')
      const materialsData = response.data || response || []
      
      // Validate and clean the materials data
      const cleanedMaterials = materialsData.map((material: any) => ({
        ...material,
        currentStock: Number(material.currentStock) || 0,
        reorderPoint: Number(material.reorderPoint) || 0,
        averageCost: material.averageCost ? Number(material.averageCost) : null,
        lastCostPerUnit: material.lastCostPerUnit ? Number(material.lastCostPerUnit) : null,
        lastPurchasePrice: material.lastPurchasePrice ? Number(material.lastPurchasePrice) : null,
        specifications: material.specifications || {},
        imageUrls: material.imageUrls || []
      }))
      
      setMaterials(cleanedMaterials)
    } catch (error) {
      console.error('Error fetching materials (optimized):', error)
      // Fallback to the slower with-purchase-info endpoint
      try {
        const fallbackResponse = await apiClient.get('/materials/with-purchase-info')
        const fallbackData = fallbackResponse.data || fallbackResponse || []
        const cleanedFallbackMaterials = fallbackData.map((material: any) => ({
          ...material,
          currentStock: Number(material.currentStock) || 0,
          reorderPoint: Number(material.reorderPoint) || 0,
          averageCost: material.averageCost ? Number(material.averageCost) : null,
          lastCostPerUnit: material.lastCostPerUnit ? Number(material.lastCostPerUnit) : null,
          lastPurchasePrice: material.lastPurchasePrice ? Number(material.lastPurchasePrice) : null,
          specifications: material.specifications || {},
          imageUrls: material.imageUrls || []
        }))
        setMaterials(cleanedFallbackMaterials)
      } catch (fallbackError) {
        console.error('Error fetching materials (fallback):', fallbackError)
        // Final fallback to basic materials API
        try {
          const basicResponse = await materialsApi.getAll()
          setMaterials(basicResponse || [])
        } catch (basicError) {
          console.error('Error fetching materials (basic):', basicError)
          setMaterials([])
          alert('Failed to load materials. Please refresh the page and try again.')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to format stock quantities - improved to handle decimals when appropriate
  const formatStock = (stock: any): string => {
    const numericStock = Number(stock) || 0
    // For most materials, use whole numbers, but allow decimals for weight/volume materials
    if (numericStock % 1 === 0) {
      return Math.floor(numericStock).toString()
    } else {
      return numericStock.toFixed(2).replace(/\.?0+$/, '')
    }
  }

  // Helper function to get unit cost with fallback - simplified and more reliable
  const getUnitCost = (material: Material): number => {
    // Helper function to safely convert to number
    const toNumber = (value: any): number => {
      if (value === null || value === undefined) return 0
      const num = typeof value === 'string' ? parseFloat(value) : Number(value)
      return isNaN(num) ? 0 : num
    }
    
    // Helper function to round cost to cents for consistency
    const roundToCents = (value: number): number => {
      return Math.round(value * 100) / 100
    }
    
    // Simplified cost priority: averageCost -> lastCostPerUnit -> lastPurchasePrice -> 0
    const averageCost = toNumber(material.averageCost)
    const lastCostPerUnit = toNumber(material.lastCostPerUnit)
    const lastPurchasePrice = toNumber(material.lastPurchasePrice)
    
    let finalCost = 0
    if (averageCost > 0) {
      finalCost = averageCost
    } else if (lastCostPerUnit > 0) {
      finalCost = lastCostPerUnit
    } else if (lastPurchasePrice > 0) {
      finalCost = lastPurchasePrice
    }
    
    finalCost = roundToCents(finalCost)
    
    // Only show warning if no cost data is available and user hasn't been warned yet
    if (finalCost === 0 && !(material as any)._costWarningShown) {
      console.warn(`Material "${material.name}" has no cost data available`)
      // Mark as warned to prevent repeated alerts
      ;(material as any)._costWarningShown = true
    }
    
    return finalCost
  }

  const handleSubmitAdjustment = async (): Promise<void> => {
    // Validate required fields
    if (!selectedMaterial) {
      alert('Please select a material')
      return
    }
    
    if (!quantity || quantity.trim() === '') {
      alert('Please enter a quantity')
      return
    }
    
    if (!reason || reason.trim() === '') {
      alert('Please select a reason for the adjustment')
      return
    }

    if (isWriteOff && (!writeOffCategory || writeOffCategory.trim() === '')) {
      alert('Please select a write-off category when marking as tax write-off')
      return
    }

    // Validate quantity is a positive number
    const adjustmentQuantity = Number(quantity)
    if (isNaN(adjustmentQuantity) || adjustmentQuantity <= 0) {
      alert('Please enter a valid positive number')
      return
    }
    
    // For most materials, enforce whole numbers, but allow decimals for weight/volume materials
    const unitName = selectedMaterial.templateUnitType?.name || selectedMaterial.templateUnitType?.abbreviation || ''
    const allowDecimals = ['kg', 'g', 'lb', 'oz', 'l', 'ml', 'gal', 'm', 'cm', 'mm', 'ft', 'in', 'kilogram', 'gram', 'pound', 'ounce', 'liter', 'milliliter', 'gallon', 'meter', 'centimeter', 'millimeter', 'foot', 'inch'].includes(unitName.toLowerCase())
    
    if (!allowDecimals && !Number.isInteger(adjustmentQuantity)) {
      alert('Please enter a whole number for this material type (no fractions allowed)')
      return
    }

    setIsLoading(true)
    try {
      // Calculate financial impact with proper type safety
      let actualQuantityChange: number = 0
      const currentStock = Number(selectedMaterial.currentStock) || 0
      
      switch (adjustmentType) {
        case 'increase':
          actualQuantityChange = adjustmentQuantity
          break
        case 'decrease':
          actualQuantityChange = -adjustmentQuantity
          break
        case 'set':
          actualQuantityChange = adjustmentQuantity - currentStock
          break
        default:
          throw new Error(`Invalid adjustment type: ${adjustmentType}`)
      }
      
      const unitCost = getUnitCost(selectedMaterial)
      // CRITICAL FIX: Cost impact should be positive for increases, negative for decreases
      const totalCostImpact = actualQuantityChange * unitCost
      
      const adjustmentData = {
        materialId: selectedMaterial.id,
        adjustmentType,
        quantity: adjustmentQuantity,
        reason,
        notes,
        unitCost: unitCost,
        totalCostImpact,
        isWriteOff,
        writeOffCategory: isWriteOff ? writeOffCategory : undefined,
        adjustedBy: 'Current User' // In real app, get from auth context
      }

      // Call the comprehensive stock adjustment API
      const result = await apiClient.post('/materials/stock-adjustment-comprehensive', adjustmentData)
      console.log('Stock adjustment completed:', result)

      // Update local state - simplified response handling
      let newCurrentStock = selectedMaterial.currentStock
      
      // Handle the expected response structure from stock-adjustment-comprehensive
      if (result.data?.material?.currentStock !== undefined) {
        newCurrentStock = Number(result.data.material.currentStock)
      } else {
        // Calculate the new stock manually as fallback
        const currentStockNum = Number(selectedMaterial.currentStock)
        switch (adjustmentType) {
          case 'increase':
            newCurrentStock = currentStockNum + adjustmentQuantity
            break
          case 'decrease':
            newCurrentStock = Math.max(0, currentStockNum - adjustmentQuantity)
            break
          case 'set':
            newCurrentStock = adjustmentQuantity
            break
        }
      }

      const updatedMaterials = materials.map(material => {
        if (material.id === selectedMaterial.id) {
          return { ...material, currentStock: newCurrentStock }
        }
        return material
      })
      setMaterials(updatedMaterials)

      // Reset form
      setSelectedMaterial(null)
      setQuantity('')
      setReason('')
      setNotes('')
      setIsWriteOff(false)
      setWriteOffCategory('')
      
      alert('Stock adjustment completed successfully!')
    } catch (error: any) {
      console.error('Error submitting adjustment:', error)
      
      // Provide more specific error messages
      let errorMessage = 'Error submitting adjustment. Please try again.'
      
      if (error?.response?.status === 400) {
        errorMessage = 'Invalid adjustment data. Please check your inputs and try again.'
      } else if (error?.response?.status === 404) {
        errorMessage = 'Material not found. Please refresh the page and try again.'
      } else if (error?.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again in a moment.'
      } else if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
        errorMessage = 'Network connection error. Please check your connection and try again.'
      } else if (error?.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`
      }
      
      alert(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const getAdjustmentPreview = () => {
    if (!selectedMaterial || !quantity) return null

    // Handle both whole numbers and decimals appropriately
    const currentStock = Number(selectedMaterial.currentStock)
    const adjustmentAmount = Number(quantity)
    
    if (isNaN(currentStock) || isNaN(adjustmentAmount)) return null
    
    let newStock = currentStock
    let actualQuantityChange = 0

    switch (adjustmentType) {
      case 'increase':
        newStock = currentStock + adjustmentAmount
        actualQuantityChange = adjustmentAmount
        break
      case 'decrease':
        newStock = Math.max(0, currentStock - adjustmentAmount)
        actualQuantityChange = newStock - currentStock
        break
      case 'set':
        newStock = adjustmentAmount
        actualQuantityChange = newStock - currentStock
        break
    }

    const unitCost = getUnitCost(selectedMaterial)
    const costImpact = actualQuantityChange * unitCost

    return {
      currentStock,
      newStock,
      difference: newStock - currentStock,
      costImpact,
      isDecrease: actualQuantityChange < 0
    }
  }

  const preview = getAdjustmentPreview()

  return (
    <>
      <Helmet>
        <title>Stock Adjustment - Jewelry Inventory Tracker</title>
      </Helmet>
      
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/materials')}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Materials
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Stock Adjustment
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Adjust inventory quantities for physical counts, damage, or corrections
            </p>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Material Selection */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Select Material</h2>
          
          <div className="mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <option value="">All Categories</option>
                  {materialCategories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Stock Level
                </label>
                <Select
                  value={stockFilter}
                  onValueChange={setStockFilter}
                >
                  <option value="">All Stock Levels</option>
                  <option value="out">Out of Stock</option>
                  <option value="low">Low Stock</option>
                  <option value="normal">Normal Stock</option>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3 text-sm text-gray-600 dark:text-gray-400">
            <span>
              {filteredMaterials.length} of {materials.length} materials
              {(searchTerm || categoryFilter || stockFilter) && ' (filtered)'}
            </span>
            {(searchTerm || categoryFilter || stockFilter) && (
              <button
                onClick={() => {
                  setSearchTerm('')
                  setCategoryFilter('')
                  setStockFilter('')
                }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Clear filters
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading materials...</span>
              </div>
            ) : filteredMaterials.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No materials found</p>
                {(searchTerm || categoryFilter || stockFilter) && (
                  <p className="text-sm mt-2">Try adjusting your filters</p>
                )}
              </div>
            ) : (
              filteredMaterials.map((material) => {
                // Helper function to format specifications for display
                const formatSpecifications = (specs: any) => {
                  if (!specs || typeof specs !== 'object') return []
                  
                  return Object.entries(specs)
                    .filter(([key, value]) => value && value !== '')
                    .map(([key, value]) => {
                      // Format key names to be more readable
                      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
                      return `${formattedKey}: ${value}`
                    })
                }

                const specifications = formatSpecifications(material.specifications)
                
                return (
                  <div
                    key={material.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedMaterial?.id === material.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedMaterial(material)}
                  >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      {/* Material Name and SKU */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">
                          {material.name}
                        </h3>
                        {material.sku && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                            SKU: {material.sku}
                          </span>
                        )}
                      </div>
                      
                      {/* Brand */}
                      {material.brand && (
                        <p className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                          Brand: {material.brand}
                        </p>
                      )}
                      
                      {/* Category and Basic Info */}
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Category: {material.templateCategory?.name}
                        {material.color && ` • Color: ${material.color}`}
                        {material.size && ` • Size: ${material.size}`}
                      </p>
                      
                      {/* Description */}
                      {material.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                          {material.description}
                        </p>
                      )}
                      
                      {/* Specifications */}
                      {specifications.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {specifications.slice(0, 3).map((spec, index) => (
                            <span
                              key={index}
                              className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded"
                            >
                              {spec}
                            </span>
                          ))}
                          {specifications.length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">
                              +{specifications.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Stock Information */}
                    <div className="text-right ml-4">
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatStock(material.currentStock)} {material.templateUnitType?.abbreviation || 'units'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        Reorder: {formatStock(material.reorderPoint)}
                      </div>
                      {material.currentStock <= material.reorderPoint && (
                        <Badge variant="destructive" size="sm">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Low Stock
                        </Badge>
                      )}
                    </div>
                  </div>
                  </div>
                )
              })
            )}
          </div>
        </Card>

        {/* Adjustment Form */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Adjustment Details</h2>
          
          {selectedMaterial ? (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  Selected Material
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="font-medium text-lg">{selectedMaterial.name}</div>
                  
                  {/* SKU and Brand */}
                  <div className="flex gap-4 mt-1 mb-2">
                    {selectedMaterial.sku && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        SKU: <span className="font-medium">{selectedMaterial.sku}</span>
                      </span>
                    )}
                    {selectedMaterial.brand && (
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        Brand: <span className="font-medium">{selectedMaterial.brand}</span>
                      </span>
                    )}
                  </div>
                  
                  {/* Stock Information */}
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Current Stock: <span className="font-medium">{selectedMaterial.currentStock} {selectedMaterial.templateUnitType?.abbreviation || 'units'}</span>
                    {selectedMaterial.reorderPoint && (
                      <span className="ml-4">Reorder Point: {selectedMaterial.reorderPoint}</span>
                    )}
                  </div>
                  
                  {/* Category and Basic Properties */}
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Category: <span className="font-medium">{selectedMaterial.templateCategory?.name}</span>
                    {selectedMaterial.color && (
                      <span className="ml-4">Color: <span className="font-medium">{selectedMaterial.color}</span></span>
                    )}
                    {selectedMaterial.size && (
                      <span className="ml-4">Size: <span className="font-medium">{selectedMaterial.size}</span></span>
                    )}
                  </div>
                  
                  {/* Description */}
                  {selectedMaterial.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span className="font-medium">Description:</span> {selectedMaterial.description}
                    </div>
                  )}
                  
                  {/* Specifications */}
                  {selectedMaterial.specifications && Object.keys(selectedMaterial.specifications).length > 0 && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Specifications:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {Object.entries(selectedMaterial.specifications)
                          .filter(([key, value]) => value && value !== '')
                          .map(([key, value]) => (
                            <span
                              key={key}
                              className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded text-xs"
                            >
                              {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}: {value}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Adjustment Type
                </label>
                <Select
                  value={adjustmentType}
                  onValueChange={(value) => setAdjustmentType(value as 'increase' | 'decrease' | 'set')}
                >
                  <option value="increase">Increase Stock</option>
                  <option value="decrease">Decrease Stock</option>
                  <option value="set">Set Stock Level</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity ({selectedMaterial.templateUnitType?.abbreviation || 'units'})
                </label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={quantity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuantity(e.target.value)}
                  placeholder={adjustmentType === 'set' ? 'New stock level' : 'Adjustment amount'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason *
                </label>
                <Select
                  value={reason}
                  onValueChange={setReason}
                >
                  <option value="">Select a reason...</option>
                  {ADJUSTMENT_REASONS.map((reasonOption) => (
                    <option key={reasonOption} value={reasonOption}>
                      {reasonOption}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Additional notes about this adjustment..."
                />
              </div>

              {/* Write-off Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="isWriteOff"
                    checked={isWriteOff}
                    onChange={(e) => setIsWriteOff(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isWriteOff" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mark as tax write-off
                  </label>
                </div>
                
                {isWriteOff && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Write-off Category *
                    </label>
                    <Select
                      value={writeOffCategory}
                      onValueChange={setWriteOffCategory}
                    >
                      <option value="">Select write-off category...</option>
                      {WRITE_OFF_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              {preview && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Adjustment Preview
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Current Stock:</span>
                      <span>{preview.currentStock} {selectedMaterial.templateUnitType?.abbreviation || 'units'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>New Stock:</span>
                      <span className="font-medium">{preview.newStock} {selectedMaterial.templateUnitType?.abbreviation || 'units'}</span>
                    </div>
                    <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-1">
                      <span>Change:</span>
                      <span className={`font-medium ${
                        preview.difference > 0 ? 'text-green-600' : preview.difference < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {preview.difference > 0 ? '+' : ''}{preview.difference} {selectedMaterial.templateUnitType?.abbreviation || 'units'}
                      </span>
                    </div>
                    
                    {/* Financial Impact */}
                    <div className="border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span>Unit Cost:</span>
                        <span>${getUnitCost(selectedMaterial).toFixed(2)} per {selectedMaterial.templateUnitType?.abbreviation}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cost Impact:</span>
                        <span className={`font-medium ${
                          preview.costImpact < 0 ? 'text-red-600' : preview.costImpact > 0 ? 'text-green-600' : 'text-gray-600'
                        }`}>
                          {preview.costImpact >= 0 ? '+' : ''}${preview.costImpact.toFixed(2)}
                        </span>
                      </div>
                      
                      {isWriteOff && (
                        <div className="flex justify-between text-orange-600 dark:text-orange-400">
                          <span>Tax Write-off:</span>
                          <span className="font-medium">
                            {writeOffCategory || 'Category required'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  onClick={handleSubmitAdjustment}
                  disabled={!quantity || !reason || (isWriteOff && !writeOffCategory) || isLoading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    'Save Stock Adjustment'
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setSelectedMaterial(null)
                    setQuantity('')
                    setReason('')
                    setNotes('')
                    setIsWriteOff(false)
                    setWriteOffCategory('')
                  }}
                >
                  Clear Form
                </Button>
              </div>

              {/* Help text for disabled button */}
              {(!quantity || !reason || (isWriteOff && !writeOffCategory)) && (
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">Required fields missing:</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700 dark:text-yellow-300">
                    {!quantity && <li>Enter adjustment quantity</li>}
                    {!reason && <li>Select adjustment reason</li>}
                    {isWriteOff && !writeOffCategory && <li>Select write-off category</li>}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a material from the list to make an adjustment</p>
            </div>
          )}
        </Card>
      </div>
    </div>
    </>
  )
}